// ❗ WARNING please use your access token from mapbox.com
<script setup lang="ts">
import type { LngLatLike } from 'mapbox-gl'
import mapboxgl from 'mapbox-gl'
import { onMounted, ref } from 'vue'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { useDisplay } from 'vuetify'
import fleetImg from '@images/misc/fleet-car.png'
import SetDriverToDrivingGroupDrawer from '@/views/apps/ecommerce/logistics/SetDriverToDrivingGroupDrawer.vue'
import type { external } from '@/schema.d'
import { DrivingActionStatusEnum } from '@/enums'

type DrivingGroupResponse =
  external['components/schemas/driver/DrivingDailyGroup.yaml']
type DriverDetailResponse =
  external['components/schemas/response/admin/driver/DriverDetailResponse.yaml']
const route = useRoute('apps-ecommerce-logistics-groups-details-id')

const { isLeftSidebarOpen } = useResponsiveLeftSidebar()
const isSetDriverToDrivingGroupDrawerOpen = ref(false)

const accessToken
  = 'pk.eyJ1Ijoib3BlcmkiLCJhIjoiY2xzY2I1NmtyMG05eDJxb2F4cG8xamgxYiJ9.Yv1JfI1-rBmD2CsNasITCQ'

const map = ref()

const vuetifyDisplay = useDisplay()

const { data: drivingGroup, execute: fetchDrivingGroup }
  = await useApiOperi<DrivingGroupResponse>(
    `admin/driving/group/${route.params.id}`,
  )

const { data: driverData } = await useApiOperi<DriverDetailResponse>(
  createUrl(`admin/drivers/${drivingGroup.value?.driverId}`),
)

definePage({
  meta: {
    layoutWrapperClasses: 'layout-content-height-fixed',
  },
})

const carImgs = ref<Array<string>>([])
const showPanel = ref<Array<boolean>>([])

drivingGroup.value?.drivingTasks!.forEach(_ => {
  carImgs.value.push(fleetImg)
  showPanel.value.push(false)
})

const refCars = ref<HTMLElement[]>([])

const activeIndex = ref(0)

const getMarkerForStatus = (status: string | undefined): string => {
  if (status === DrivingActionStatusEnum.ACTIVE)
    return 'action-marker-active'
  else if (status === DrivingActionStatusEnum.CANCELLED)
    return 'action-marker-canceled'
  else if (status === DrivingActionStatusEnum.FAILURE)
    return 'action-marker-delayed'
  else if (status === DrivingActionStatusEnum.SUCCESS)
    return 'action-marker-completed'

  return ''
}

onMounted(() => {
  mapboxgl.accessToken = accessToken

  map.value = new mapboxgl.Map({
    container: 'mapContainer',
    style: 'mapbox://styles/mapbox/light-v9',
    center: [20.456092, 44.801971],
    zoom: 12.25,
  })

  for (
    let index = 0;
    index < drivingGroup.value!.drivingTasks!.length;
    index++
  ) {
    new mapboxgl.Marker({ element: refCars.value[index] })
      .setLngLat(
        drivingGroup.value?.drivingTasks!.at(index)?.coordinates as LngLatLike,
      )
      .addTo(map.value)

    const actionStatusColor = getMarkerForStatus(
      drivingGroup.value!.drivingTasks!.at(index)?.status,
    )

    if (actionStatusColor !== '')
      refCars.value[index].classList.add(actionStatusColor)
  }

  refCars.value[activeIndex?.value]?.classList?.add('blink_me')
})

watch(activeIndex, () => {
  refCars.value.forEach((car, index) => {
    if (index === activeIndex.value)
      car.classList.add('blink_me')
    else car.classList.remove('blink_me')
  })
})

watch(isSetDriverToDrivingGroupDrawerOpen, async () => {
  console.log('driver batoo')
  await fetchDrivingGroup()
  driverData.value = (
    await useApiOperi<DriverDetailResponse>(
      createUrl(`admin/drivers/${drivingGroup.value?.driverId}`),
    )
  ).data.value
})

const flyToLocation = (geolocation: LngLatLike, index: number) => {
  activeIndex.value = index
  showPanel.value.fill(false)
  showPanel.value[index] = !showPanel.value[index]

  if (vuetifyDisplay.mdAndDown.value)
    isLeftSidebarOpen.value = false

  map.value.flyTo({
    center: geolocation,
    zoom: 14,
  })
}
</script>

<template>
  <VCardText>
    <div class="d-flex justify-space-between flex-wrap gap-y-4">
      <div class="d-flex flex-row gap-4 align-center flex-wrap">
        <VBtn
          v-if="driverData !== isNullOrUndefined"
          prepend-icon="tabler-arrows-left-right"
          @click="
            isSetDriverToDrivingGroupDrawerOpen
              = !isSetDriverToDrivingGroupDrawerOpen
          "
        >
          Change Driver
        </VBtn>
        <VBtn
          v-if="driverData === isNullOrUndefined"
          prepend-icon="tabler-plus"
          @click="
            isSetDriverToDrivingGroupDrawerOpen
              = !isSetDriverToDrivingGroupDrawerOpen
          "
        >
          Add Driver
        </VBtn>
      </div>
    </div>
  </VCardText>
  <VLayout class="fleet-app-layout">
    <VNavigationDrawer
      v-model="isLeftSidebarOpen"
      width="320"
      absolute
      touchless
      location="start"
    >
      <VCard
        class="h-100 fleet-navigation-drawer"
        flat
      >
        <VCardItem>
          <VRow>
            <VCol>
              <VCardTitle>
                {{ driverData?.fullName }}
              </VCardTitle>
            </VCol>
          </VRow>
          <template #append>
            <IconBtn
              class="d-lg-none navigation-close-btn"
              @click="isLeftSidebarOpen = !isLeftSidebarOpen"
            >
              <VIcon icon="tabler-x" />
            </IconBtn>
          </template>
        </VCardItem>

        <!-- 👉 Perfect Scrollbar -->
        <PerfectScrollbar
          :options="{ wheelPropagation: false, suppressScrollX: true }"
          style="block-size: calc(100% - 60px)"
        >
          <VCardText class="pt-0">
            <div
              v-for="(drivingAction, index) in drivingGroup?.drivingTasks"
              :key="index"
              class="mb-6"
            >
              <div
                class="d-flex align-center justify-space-between cursor-pointer"
                @click="
                  flyToLocation(drivingAction?.coordinates as LngLatLike, index)
                "
              >
                <div class="d-flex gap-x-4">
                  <VAvatar
                    icon="tabler-truck"
                    variant="tonal"
                  />
                  <div>
                    <div class="text-body-1 text-high-emphasis font-weight-medium">
                      {{
                        `${drivingAction?.streetName} ${drivingAction?.streetNumber}`
                      }}
                    </div>
                    <div class="text-body-1 text-disabled">
                      {{ drivingAction.type }}
                    </div>
                  </div>
                </div>
                <IconBtn density="comfortable">
                  <VIcon
                    :icon="
                      showPanel[index]
                        ? 'tabler-chevron-down'
                        : $vuetify.locale.isRtl
                          ? 'tabler-chevron-left'
                          : 'tabler-chevron-right'
                    "
                  />
                </IconBtn>
              </div>
              <VExpandTransition mode="out-in">
                <div v-show="showPanel[index]">
                  <VCol cols="12">
                    <div class="text-caption text-uppercase text-success font-weight-medium">
                      {{ `Time: ${drivingAction.time}` }}
                    </div>
                  </VCol>
                  <VCol cols="12">
                    <div>
                      {{ `Instructions: ${drivingAction.instructions}` }}
                    </div>
                  </VCol>
                  <VCol cols="12">
                    <div>
                      {{ `Status: ${drivingAction.status}` }}
                    </div>
                  </VCol>
                </div>
              </VExpandTransition>
            </div>
          </VCardText>
        </PerfectScrollbar>
      </VCard>
    </VNavigationDrawer>

    <VMain>
      <div class="h-100">
        <IconBtn
          class="d-lg-none navigation-toggle-btn rounded-sm"
          variant="elevated"
          @click="isLeftSidebarOpen = true"
        >
          <VIcon icon="tabler-menu-2" />
        </IconBtn>
        <!-- 👉 Fleet map  -->
        <div
          id="mapContainer"
          class="basemap"
        />

        <img
          v-for="(car, index) in carImgs"
          :key="index"
          ref="refCars"
          :src="car"
          alt="car Img marker"
          height="42"
          width="20"
        >
      </div>
    </VMain>
  </VLayout>

  <SetDriverToDrivingGroupDrawer
    v-model:is-drawer-open="isSetDriverToDrivingGroupDrawerOpen"
    v-model:driving-group-id="drivingGroup!.drivingGroupId"
  />
</template>

<style lang="scss">
@use "@styles/variables/_vuetify.scss";
@use "@core/scss/base/_mixins.scss";
@import "mapbox-gl/dist/mapbox-gl.css";

.fleet-app-layout {
  border-radius: vuetify.$card-border-radius;

  @include mixins.elevation(vuetify.$card-elevation);

  $sel-fleet-app-layout: &;

  @at-root {
    .skin--bordered {
      @include mixins.bordered-skin($sel-fleet-app-layout);
    }
  }
}

.navigation-toggle-btn {
  position: absolute;
  z-index: 1;
  inset-block-start: 1rem;
  inset-inline-start: 1rem;
}

.navigation-close-btn {
  position: absolute;
  z-index: 1;
  inset-block-start: 1rem;
  inset-inline-end: 1rem;
}

.basemap {
  block-size: 100%;
  inline-size: 100%;
}

.action-marker-active {
  filter: drop-shadow(0 0 7px blue);
}

.action-marker-completed {
  filter: drop-shadow(0 0 7px green);
}

.action-marker-canceled {
  filter: drop-shadow(0 0 7px red);
}

.action-marker-delayed {
  filter: drop-shadow(0 0 7px orange);
}

.blink_me {
  animation: blinker 1s linear infinite;
}

@keyframes blinker {
  50% {
    opacity: 0.5;
  }
}

.mapboxgl-ctrl-bottom-left,
.mapboxgl-ctrl-bottom-right {
  display: none;
}

.fleet-navigation-drawer {
  .v-timeline .v-timeline-divider__dot .v-timeline-divider__inner-dot {
    box-shadow: none;
  }
}

#mapContainer {
  block-size: 100vh !important;
}
</style>
